import { FetcherResponseType } from "@website/fetcher/fetcher";
import {
  BlobFetcherType,
  FetcherType,
  RejectHandlerType
} from "@website/fetcher/fetcher/fetcher.types";
import { redirectShab } from "@website/fetcher/redirect-shab";
import { forceLogin, refreshToken } from "@website/fetcher/refresh-token";
import { authStore } from "@website/store/store";
import { getTracking } from "@website/store/tracking";
import { getChannel } from "@website/utils/channel/get-channel";
import { Channels } from "@website/utils/channel/types";
import { isSSR } from "@website/utils/is-ssr";
import { replaceOptions } from "@website/utils/replace-options";
import isArray from "lodash-es/isArray";
import isPlainObject from "lodash-es/isPlainObject";
import mapValues from "lodash-es/mapValues";
import omit from "lodash-es/omit";
import toString from "lodash-es/toString";
import values from "lodash-es/values";

const cache: Record<string, { expire: number; data: FetcherResponseType }> = {};

const rejectHandler: RejectHandlerType = ({ reject, error, extra }) => {
  reject({ error, ...extra });
  // eslint-disable-next-line no-console
  // isSSR() && console.error({ error, ...extra, date: new Date() });
  // captureException(error, {
  //   extra,
  //   tags: {
  //     errorType: "apiCallError",
  //     ApiUrl: toString(extra.url),
  //     ApiStatus: toString(extra.status)
  //   }
  // });
};

const getAuthStore = () => {
  try {
    const storage = isSSR()
      ? {}
      : localStorage.getItem("auth_store") &&
        JSON.parse(String(localStorage.getItem("auth_store")));
    return storage || {}; // Return the parsed storage or an empty object if parsing fails or localStorage is unavailable.
  } catch {
    return {};
  }
};
const convertObjectToErrorMessage = (data: unknown): string => {
  if (typeof data === "string") {
    return data;
  }
  if (Array.isArray(data)) {
    return data.map(convertObjectToErrorMessage).join(" & ");
  }
  if (typeof data === "object" && data !== null) {
    return Object.values(data).map(convertObjectToErrorMessage).join(" & ");
  }
  return "";
};
const buildError = (
  response: FetcherResponseType,
  configs: Record<string, unknown>,
  headers: Record<string, unknown>
) => ({
  error: `API call Error => status:${
    response.status
  } data: ${convertObjectToErrorMessage(response.data)}`,
  extra: {
    ...omit(configs, "validateStatus"),
    headers,
    response: response.data,
    status: response.status
  }
});

export const fetcher: FetcherType = (configs, openLoginParameter) => {
  const cacheKey = toString(configs?.next?.tags?.toString?.());
  const isCacheable =
    isSSR() &&
    cacheKey &&
    !["no-cache", "no-store"].includes(configs?.cache || "default");
  return new Promise((resolve, reject) => {
    if (isCacheable && cache[cacheKey]?.data) {
      if (cache[cacheKey]?.expire >= Math.floor(Date.now() / 1000)) {
        return resolve(cache[cacheKey].data);
      }
      resolve(cache[cacheKey].data);
    }
    const url = String(
      configs.options
        ? replaceOptions(configs.url, configs.options)
        : configs.url
    );
    const channel = getChannel();

    const storage = getAuthStore();
    const accessToken = isSSR() ? "" : authStore.accessToken.get();
    const jekToken = isSSR() ? "" : storage.jekToken;

    const trackingKey = getTracking().sid;
    const experimentKeys = values(
      mapValues(getTracking().experiment, (value, key) => `${key}_${value}`)
    ).join(",");
    const isFormData = configs.data instanceof FormData;
    const headers = {
      Accept: "application/json",
      "Accept-Language": "fa",
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      ...((channel ? { channel } : {}) as { channel?: Channels }),
      ...(accessToken || jekToken
        ? { Authorization: accessToken ? `Bearer ${accessToken}` : jekToken }
        : {}),
      ...(trackingKey ? { "User-Tracking-Key": toString(trackingKey) } : {}),
      ...(experimentKeys ? { "Experiment-Keys": experimentKeys } : {}),
      ...configs.headers
    };
    fetch(
      configs.params
        ? `${url}?${Object.entries(configs.params)
            .map(([key, value]) =>
              isArray(value)
                ? value.map((value) => `${key}=${value}`).join("&")
                : `${key}=${value}`
            )
            .join("&")}`
        : url,
      {
        cache: configs.cache ?? "default",
        method: configs.method ?? "GET",
        headers,
        body: isFormData
          ? (configs.data as BodyInit)
          : JSON.stringify(configs.data),
        ...(configs.credentials && { credentials: configs.credentials })
      }
    )
      .then((response) =>
        response
          .json()
          .then((data) => ({
            ...omit(response, ["body", "bodyUsed"]),
            data
          }))
          .catch((error) => ({
            ...omit(response, ["body", "bodyUsed"]),
            data: response.ok ? {} : error
          }))
      )
      .then((response) => {
        if (
          (response.ok || configs.validateStatus?.(response.status)) &&
          (isPlainObject(response.data?.error)
            ? Object.keys(response.data?.error).length === 0
            : !response.data?.error) &&
          ![
            toString(response.data?.success),
            toString(response.data?.successful),
            toString(response.data?.is_successfull)
          ].includes("false")
        ) {
          if (isCacheable) {
            const revalidate = configs.next?.revalidate;
            cache[cacheKey] = {
              expire:
                Math.floor(Date.now() / 1000) +
                (revalidate === false || revalidate === undefined
                  ? Number.POSITIVE_INFINITY
                  : revalidate),
              data: response
            };
          }
          return resolve(response);
        }
        if (
          response.status === 401 &&
          !isSSR() &&
          typeof window !== "undefined"
        ) {
          if (authStore.refreshToken.get()) {
            return refreshToken()
              .then(() => resolve(fetcher(configs)))
              .catch(() => {
                if (getChannel() === "shab") {
                  redirectShab();
                  return rejectHandler({
                    reject,
                    ...buildError(response, configs, headers)
                  });
                }
                if (openLoginParameter) {
                  return forceLogin(openLoginParameter)
                    .then(() => resolve(fetcher(configs)))
                    .catch(() => {
                      rejectHandler({
                        reject,
                        ...buildError(response, configs, headers)
                      });
                    });
                }
                rejectHandler({
                  reject,
                  ...buildError(response, configs, headers)
                });
              });
          }
          if (getChannel() === "shab") {
            redirectShab();
            return rejectHandler({
              reject,
              ...buildError(response, configs, headers)
            });
          }
          if (openLoginParameter) {
            return forceLogin(openLoginParameter)
              .then(() => resolve(fetcher(configs)))
              .catch(() => {
                rejectHandler({
                  reject,
                  ...buildError(response, configs, headers)
                });
              });
          }
        }
        rejectHandler({
          reject,
          ...buildError(response, configs, headers)
        });
      });
  });
};

export const blobFetcher: BlobFetcherType = async (configs) => {
  const { options, url: URLConfig, method, data, params } = configs ?? {};
  const storage = JSON.parse(localStorage.getItem("auth_store") || "{}");
  const accessToken = authStore.accessToken.get();
  const { jekToken } = storage;
  const url = String(options ? replaceOptions(URLConfig, options) : URLConfig);
  const response = await fetch(
    params
      ? `${url}?${Object.entries(params)
          .map(([key, value]) =>
            isArray(value)
              ? value.map((value) => `${key}=${value}`).join("&")
              : `${key}=${value}`
          )
          .join("&")}`
      : url,
    {
      method: method || "GET",
      body: JSON.stringify(data),
      headers: {
        Accept: "application/json",
        "Accept-Language": "fa",
        ...(accessToken || jekToken
          ? { Authorization: accessToken ? `Bearer ${accessToken}` : jekToken }
          : {})
      }
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response;
};
